import type { LevelRun } from '../core/level-runner';
import type { CoreEvent } from '../core/tick';
import { isLevelId } from '../data/ids';
import type { JumpGrade } from '../data/tuning';
import { buildRecount, type Recount } from './recount';

/**
 * Counts the scoring events of a run (issue #62) and builds its recount when the level is completed. A game
 * over restores the score of the level start, so the counts start again with it.
 */
export function createLevelTally(run: LevelRun, onRecount: (recount: Recount) => void): (event: CoreEvent) => void {
  const { def } = run.level;
  const previousBest = isLevelId(def.id) ? (run.state.completedLevels[def.id]?.bestScore ?? null) : null;
  const previousUnassisted = isLevelId(def.id) ? run.state.completedLevels[def.id]?.assisted === false : false;
  // Taken now: completing the level moves scoreAtLevelStart on to the start of the next one (#41).
  const scoreAtLevelStart = run.state.scoreAtLevelStart;
  const fresh = () => ({ columns: 0, dollars: 0, checkpoints: 0, armorBonuses: 0, decoyHits: 0, coins: 0, coinPoints: 0, bossDefeated: false, jump: null as JumpGrade | null });
  let counts = fresh();
  const reset = () => (counts = fresh());

  return (event) => {
    switch (event.type) {
      case 'COLUMN_PASSED':
        counts.columns += 1;
        break;
      case 'DOLLAR_COLLECTED':
        counts.dollars += 1;
        break;
      case 'CHECKPOINT_ACTIVATED':
        counts.checkpoints += 1;
        break;
      case 'powerup:armor-duplicate':
        counts.armorBonuses += 1;
        break;
      // The boss fight and El Salto (#89, #90, #93): the boss, each decoy hit and the grade of the landing.
      case 'boss:defeated':
        counts.bossDefeated = true;
        break;
      case 'decoy:hit':
        counts.decoyHits += 1;
        break;
      // The silver coins of the boss attacks (#95), worth their own points.
      case 'boss:coin':
        counts.coins += 1;
        counts.coinPoints += Number(event.points);
        break;
      case 'jump:delivered':
        counts.jump = event.grade as JumpGrade;
        break;
      case 'RUN_RESTORED':
        reset();
        break;
      case 'LEVEL_COMPLETED':
        if (!isLevelId(def.id)) break;
        onRecount(
          buildRecount({
            levelId: def.id,
            ending: def.ending,
            ...counts,
            landed: false,
            cleanRun: Boolean(event.cleanRun),
            scoreAtLevelStart,
            previousBest,
            assisted: run.state.assisted,
            previousUnassisted,
          }),
        );
        break;
    }
  };
}
